import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useModalize } from 'react-native-modalize'

import Dialog from '@common/components/Dialog'
import DialogButton from '@common/components/Dialog/DialogButton'
import DialogFooter from '@common/components/Dialog/DialogFooter'
import Input from '@common/components/Input'
import spacings from '@common/styles/spacings'

import {
  cancelLatticePrompt,
  type LatticePromptKind,
  resolveLatticePrompt,
  subscribeLatticePrompt
} from '../../services/hardwareWallet/latticePromptBridge'

// Styled replacement for the original `window.prompt` used by the mobile-web
// GridPlus/Lattice manual pairing flow. Subscribes to `latticePromptBridge`: when
// `latticePageController` needs the device id or the one-time pairing secret it
// opens this Dialog, and the entered value resolves the controller's awaiting
// promise. Mounted once, globally, from `AppInit`.

const COPY: Record<LatticePromptKind, { title: string; text: string; label: string; cta: string }> =
  {
    deviceId: {
      title: 'Connect your Lattice1',
      text: 'Enter your Lattice1 Device ID. You can find it on the device under Settings → Device → Device ID.',
      label: 'Device ID',
      cta: 'Connect'
    },
    pairingSecret: {
      title: 'Pair with your Lattice1',
      text: 'Your Lattice1 is now showing a one-time pairing code on its screen. Enter it below to finish pairing.',
      label: 'Pairing code',
      cta: 'Pair'
    }
  }

const LatticePairingModal = () => {
  const { t } = useTranslation()
  const { ref: dialogRef, open: openDialog, close: closeDialog } = useModalize()
  const [kind, setKind] = useState<LatticePromptKind | null>(null)
  const [value, setValue] = useState('')
  // Distinguishes a programmatic close (after submit/cancel) from a
  // user-dismissed close (backdrop/drag), so only the latter cancels the prompt.
  const settledRef = useRef(false)

  useEffect(() => {
    return subscribeLatticePrompt((req) => {
      if (req) {
        settledRef.current = false
        setValue('')
        setKind(req.kind)
        openDialog()
      } else {
        // The controller resolved/cancelled elsewhere (e.g. a superseding
        // request) — just close without re-cancelling.
        settledRef.current = true
        setKind(null)
        closeDialog()
      }
    })
  }, [openDialog, closeDialog])

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim()
    if (!trimmed) return
    settledRef.current = true
    closeDialog()
    setKind(null)
    resolveLatticePrompt(trimmed)
  }, [value, closeDialog])

  const handleCancel = useCallback(() => {
    settledRef.current = true
    closeDialog()
    setKind(null)
    cancelLatticePrompt()
  }, [closeDialog])

  // Backdrop press / drag-dismiss routes here too; treat as cancel unless the
  // close was already triggered programmatically by submit/cancel.
  const handleClosed = useCallback(() => {
    if (settledRef.current) return
    settledRef.current = true
    setKind(null)
    cancelLatticePrompt()
  }, [])

  if (!kind) {
    // Keep the Dialog mounted (ref must persist) but render no copy when idle.
    return (
      <Dialog
        id="lattice-pairing"
        dialogRef={dialogRef}
        title=""
        text=""
        closeDialog={handleClosed}
      >
        <></>
      </Dialog>
    )
  }

  const copy = COPY[kind]

  return (
    <Dialog
      id="lattice-pairing"
      dialogRef={dialogRef}
      title={t(copy.title)}
      text={t(copy.text)}
      closeDialog={handleClosed}
    >
      <Input
        label={t(copy.label)}
        value={value}
        onChangeText={setValue}
        autoFocus
        autoCapitalize="none"
        autoCorrect={false}
        onSubmitEditing={handleSubmit}
        testID="lattice-pairing-input"
      />
      <DialogFooter horizontalAlignment="justifyEnd">
        <DialogButton text={t('Cancel')} type="secondary" onPress={handleCancel} />
        <DialogButton
          style={spacings.ml}
          text={t(copy.cta)}
          type="primary"
          disabled={!value.trim()}
          onPress={handleSubmit}
          testID="lattice-pairing-submit"
        />
      </DialogFooter>
    </Dialog>
  )
}

export default LatticePairingModal
